package link.erpango.quip.ui.noteimport

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Card
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.material.TextButton
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Info
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Stable
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import compose.icons.FontAwesomeIcons
import compose.icons.fontawesomeicons.Solid
import compose.icons.fontawesomeicons.solid.Magic
import compose.icons.fontawesomeicons.solid.StickyNote
import link.erpango.quip.domain.extraction.ExtractionConfidence
import link.erpango.quip.domain.noteimport.FieldSource
import link.erpango.quip.domain.noteimport.SuggestedField
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.theme.QuipDimens
import link.erpango.quip.ui.components.QuipTextBadge
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.Res
import quip.composeapp.generated.resources.note_import_action
import quip.composeapp.generated.resources.note_import_alternative
import quip.composeapp.generated.resources.note_import_confidence_high
import quip.composeapp.generated.resources.note_import_confidence_low
import quip.composeapp.generated.resources.note_import_confidence_medium
import quip.composeapp.generated.resources.note_import_entry_subtitle
import quip.composeapp.generated.resources.note_import_entry_title
import quip.composeapp.generated.resources.note_import_missing_fields
import quip.composeapp.generated.resources.note_import_quote
import quip.composeapp.generated.resources.note_import_review_banner
import quip.composeapp.generated.resources.note_import_source_ai
import quip.composeapp.generated.resources.note_import_source_both
import quip.composeapp.generated.resources.note_import_source_note
import quip.composeapp.generated.resources.note_import_suggested
import quip.composeapp.generated.resources.note_import_use

/**
 * What the form shows under a field filled (or offered) from a note.
 *
 * @property offer a value the user can apply with one tap: the other source's alternative, or
 *   the suggestion itself when the field already had a different value typed by the user.
 */
data class FieldHint(
    val confidence: ExtractionConfidence,
    val source: FieldSource,
    val quote: String? = null,
    val offer: String? = null,
    val applyOffer: (() -> Unit)? = null,
)

/**
 * Per-form hints keyed by field. Applying a suggestion never overwrites what the user typed:
 * an empty field is filled, a field with a different value only gets an offer.
 */
@Stable
class FormSuggestions<K> {
    private val hints = mutableStateMapOf<K, FieldHint>()

    operator fun get(key: K): FieldHint? = hints[key]

    val isEmpty: Boolean get() = hints.isEmpty()

    /** The user edited the field: its hint no longer applies. */
    fun clear(key: K) {
        hints.remove(key)
    }

    fun clearAll() = hints.clear()

    /**
     * @param isEmpty whether the field currently counts as empty (it will be filled).
     * @param matches whether the field already holds the suggested value.
     */
    fun <T> apply(
        key: K,
        suggestion: SuggestedField<T>?,
        note: String,
        isEmpty: Boolean,
        matches: (T) -> Boolean,
        display: (T) -> String = { it.toString() },
        set: (T) -> Unit,
    ) {
        if (suggestion == null) return
        val quote = suggestion.span?.let { span ->
            if (span.end <= note.length) span.quote(note) else null
        }
        val base = FieldHint(suggestion.confidence, suggestion.source, quote)
        val offerValue: T? = when {
            isEmpty -> {
                set(suggestion.value)
                suggestion.alternative
            }
            matches(suggestion.value) -> null
            else -> suggestion.value
        }
        hints[key] = if (offerValue == null) base else base.copy(
            offer = display(offerValue),
            applyOffer = {
                set(offerValue)
                hints[key] = base
            },
        )
    }
}

@Composable
fun <K> rememberFormSuggestions(): FormSuggestions<K> = remember { FormSuggestions() }

/** Chips under a field: "Sugerido · confianza media", source, the note quote and an optional offer. */
@Composable
fun SuggestionMarker(hint: FieldHint?, modifier: Modifier = Modifier) {
    if (hint == null || hint.source == FieldSource.USER) return
    val (color, confidenceText) = when (hint.confidence) {
        ExtractionConfidence.HIGH -> QuipColors.colorSuccess to stringResource(Res.string.note_import_confidence_high)
        ExtractionConfidence.MEDIUM -> QuipColors.colorWarning to stringResource(Res.string.note_import_confidence_medium)
        ExtractionConfidence.LOW -> QuipColors.colorError to stringResource(Res.string.note_import_confidence_low)
    }
    val sourceText = when (hint.source) {
        FieldSource.NOTE -> stringResource(Res.string.note_import_source_note)
        FieldSource.AI -> stringResource(Res.string.note_import_source_ai)
        FieldSource.BOTH -> stringResource(Res.string.note_import_source_both)
        FieldSource.USER -> ""
    }
    Column(modifier = modifier.fillMaxWidth().padding(start = QuipDimens.padding8)) {
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(QuipDimens.padding6),
            verticalArrangement = Arrangement.spacedBy(QuipDimens.padding4),
        ) {
            QuipTextBadge(
                text = "${stringResource(Res.string.note_import_suggested)} · $confidenceText",
                backgroundColor = color.copy(alpha = 0.15f),
                textColor = color,
            )
            QuipTextBadge(text = sourceText)
        }
        hint.quote?.let { quote ->
            Text(
                text = stringResource(Res.string.note_import_quote, quote.shorten(60)),
                style = MaterialTheme.typography.caption,
                color = QuipColors.colorTextSecondary,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.padding(top = QuipDimens.padding4),
            )
        }
        if (hint.offer != null && hint.applyOffer != null) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = stringResource(Res.string.note_import_alternative, hint.offer.shorten(40)),
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorText,
                    modifier = Modifier.weight(1f, fill = false),
                )
                TextButton(onClick = hint.applyOffer) {
                    Text(stringResource(Res.string.note_import_use), fontWeight = FontWeight.Bold, color = QuipColors.colorPrimary)
                }
            }
        }
    }
}

/** "Revisa los datos antes de guardar · Faltan: VIN, color" shown after applying a note. */
@Composable
fun NoteImportReviewBanner(missingFields: List<String>, onDismiss: () -> Unit, modifier: Modifier = Modifier) {
    Card(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(QuipDimens.cornerRadius12),
        backgroundColor = QuipColors.colorWarning.copy(alpha = 0.12f),
        elevation = 0.dp,
    ) {
        Row(
            modifier = Modifier.padding(start = QuipDimens.padding12, top = QuipDimens.padding8, bottom = QuipDimens.padding8),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(Icons.Default.Info, contentDescription = null, tint = QuipColors.colorWarning)
            Spacer(Modifier.width(QuipDimens.padding8))
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.note_import_review_banner),
                    style = MaterialTheme.typography.body2,
                    fontWeight = FontWeight.Bold,
                    color = QuipColors.colorText,
                )
                if (missingFields.isNotEmpty()) {
                    Text(
                        text = stringResource(Res.string.note_import_missing_fields, missingFields.joinToString(", ")),
                        style = MaterialTheme.typography.caption,
                        color = QuipColors.colorText,
                    )
                }
            }
            IconButton(onClick = onDismiss) {
                Icon(Icons.Default.Close, contentDescription = null, tint = QuipColors.colorTextSecondary)
            }
        }
    }
}

/** Entry point shown at the top of a creation form. */
@Composable
fun NoteImportEntryCard(onClick: () -> Unit, modifier: Modifier = Modifier) {
    Card(
        modifier = modifier.fillMaxWidth().clickable(onClick = onClick),
        shape = RoundedCornerShape(QuipDimens.cornerRadius12),
        backgroundColor = QuipColors.colorPrimary.copy(alpha = 0.10f),
        elevation = 0.dp,
    ) {
        Row(modifier = Modifier.padding(QuipDimens.padding12)) {
            Icon(
                imageVector = FontAwesomeIcons.Solid.StickyNote,
                contentDescription = null,
                tint = QuipColors.colorPrimary,
                modifier = Modifier.padding(top = QuipDimens.padding2).size(QuipDimens.iconSize24),
            )
            Spacer(Modifier.width(QuipDimens.padding12))
            // Title, explanation and action stacked: side by side they squeezed each other.
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.note_import_entry_title),
                    style = MaterialTheme.typography.body1,
                    fontWeight = FontWeight.Bold,
                    color = QuipColors.colorText,
                )
                Text(
                    text = stringResource(Res.string.note_import_entry_subtitle),
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary,
                )
                Spacer(Modifier.height(QuipDimens.padding8))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(
                        imageVector = FontAwesomeIcons.Solid.Magic,
                        contentDescription = null,
                        tint = QuipColors.colorPrimary,
                        modifier = Modifier.size(QuipDimens.iconSize16),
                    )
                    Spacer(Modifier.width(QuipDimens.padding6))
                    Text(
                        text = stringResource(Res.string.note_import_action),
                        style = MaterialTheme.typography.body2,
                        fontWeight = FontWeight.Bold,
                        color = QuipColors.colorPrimary,
                    )
                }
            }
        }
    }
}

private fun String.shorten(max: Int): String =
    replace('\n', ' ').trim().let { if (it.length <= max) it else it.take(max - 1).trimEnd() + "…" }

/** A dimmed text color for disabled list rows. */
internal val DisabledText: Color get() = QuipColors.colorTextSecondary.copy(alpha = 0.6f)
